// frontend/src/hooks/usePermissions.ts

import { useState, useEffect } from 'react';
import apiService, { ModulePermission } from '../services/api';

// ✅ UPDATED: ModulePermissions interface with all permissions including order_again
interface ModulePermissions {
  view: boolean;
  add: boolean;
  edit: boolean;
  delete: boolean;
  reset_password: boolean;
  active: boolean;
  // Order-specific permissions
  track_order: boolean;
  return_exchange: boolean;
  ndr: boolean;
  rto_return: boolean;
  order_again: boolean; // ✅ ADDED: Order Again permission
  place_order: boolean; // Place Order permission
}

// ✅ UPDATED: UsePermissionsReturn with all action types including order_again
interface UsePermissionsReturn {
  permissions: ModulePermissions;
  loading: boolean;
  hasPermission: (action: 'view' | 'add' | 'edit' | 'delete' | 'reset_password' | 'active' | 'track_order' | 'return_exchange' | 'ndr' | 'rto_return' | 'order_again' | 'place_order') => boolean;
  hasAnyPermission: (actions: Array<'view' | 'add' | 'edit' | 'delete' | 'reset_password' | 'active' | 'track_order' | 'return_exchange' | 'ndr' | 'rto_return' | 'order_again' | 'place_order'>) => boolean;
  hasAllPermissions: (actions: Array<'view' | 'add' | 'edit' | 'delete' | 'reset_password' | 'active' | 'track_order' | 'return_exchange' | 'ndr' | 'rto_return' | 'order_again' | 'place_order'>) => boolean;
}

// All available modules in the system
const ALL_MODULES = [
  'dashboard',
  'products',
  'categories',
  'age_groups',
  'orders',
  'employees',
  'customers',
  'commissions',
  'reports',
  'settings',
  'slider',
  'about',
  'contact',
  'inventory',
  'reviews',
  'wallet',
  'referrals',
  'warehouse',
  'product_share',
  'cod_configuration',
  'cod_remittance'
];

export const usePermissions = (moduleName: string): UsePermissionsReturn => {
  const [permissions, setPermissions] = useState<ModulePermissions>({
    view: false,
    add: false,
    edit: false,
    delete: false,
    reset_password: false,
    active: false,
    track_order: false,
    return_exchange: false,
    ndr: false,
    rto_return: false,
    order_again: false, // ✅ ADDED: Order Again permission
    place_order: false
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadPermissions();
  }, [moduleName]);

  const loadPermissions = async () => {
    try {
      setLoading(true);
      
      // Get current user from localStorage
      const userStr = localStorage.getItem('user');
      if (!userStr) {
        console.log('No user found in localStorage');
        setLoading(false);
        return;
      }

      const user = JSON.parse(userStr);
      console.log('User role:', user.role);
      
      // If admin, give all permissions (including new ones)
      if (user.role === 'admin') {
        console.log('Admin user - granting all permissions');
        setPermissions({ 
          view: true, 
          add: true, 
          edit: true, 
          delete: true,
          reset_password: true,
          active: true,
          track_order: true,
          return_exchange: true,
          ndr: true,
          rto_return: true,
          order_again: true, // ✅ ADDED: Admin gets all permissions
          place_order: true
        });
        setLoading(false);
        return;
      }

      // If employee, use the employee endpoint (not admin)
      if (user.role === 'employee') {
        try {
          console.log('Employee user - fetching permissions from /employee/permissions');
          const response = await apiService.getMyPermissions();
          
          console.log('Permissions response:', response);
          
          if (response.success && response.data && response.data.permissions) {
            const modulePerm = response.data.permissions.find(
              (p: ModulePermission) => p.module === moduleName
            );
            
            if (modulePerm) {
              console.log(`Permissions for module ${moduleName}:`, modulePerm.permissions);
              
              // ✅ FIXED: Map database field names to frontend expected names
              // Your API returns: can_track_order, can_ndr, can_return_exchange, can_rto, can_order_again
              // Frontend expects: track_order, ndr, return_exchange, rto_return, order_again
              setPermissions({
                view: modulePerm.permissions.view || false,
                add: modulePerm.permissions.add || false,
                edit: modulePerm.permissions.edit || false,
                delete: modulePerm.permissions.delete || false,
                reset_password: (modulePerm.permissions as any).reset_password || (modulePerm.permissions as any).can_reset_password || false,
                active: (modulePerm.permissions as any).active || (modulePerm.permissions as any).can_toggle_active || false,
                // ⭐ CRITICAL FIX: Map database column names to frontend names
                track_order: (modulePerm.permissions as any).track_order || (modulePerm.permissions as any).can_track_order || false,
                return_exchange: (modulePerm.permissions as any).return_exchange || (modulePerm.permissions as any).can_return_exchange || false,
                ndr: (modulePerm.permissions as any).ndr || (modulePerm.permissions as any).can_ndr || false,
                rto_return: (modulePerm.permissions as any).rto_return || (modulePerm.permissions as any).can_rto || false,
                order_again: (modulePerm.permissions as any).order_again || (modulePerm.permissions as any).can_order_again || false, // ✅ ADDED: Order Again mapping
                place_order: (modulePerm.permissions as any).place_order || (modulePerm.permissions as any).can_place_order || false
              });
            } else {
              console.log(`No permissions found for module ${moduleName}, using defaults`);
              setPermissions({ 
                view: false, 
                add: false, 
                edit: false, 
                delete: false,
                reset_password: false,
                active: false,
                track_order: false,
                return_exchange: false,
                ndr: false,
                rto_return: false,
                order_again: false, // ✅ ADDED
                place_order: false
              });
            }
          } else {
            console.log('Invalid response structure, using defaults');
            setPermissions({ 
              view: false, 
              add: false, 
              edit: false, 
              delete: false,
              reset_password: false,
              active: false,
              track_order: false,
              return_exchange: false,
              ndr: false,
              rto_return: false,
              order_again: false, // ✅ ADDED
              place_order: false
            });
          }
        } catch (error) {
          console.error('Error fetching employee permissions:', error);
          setPermissions({ 
            view: false, 
            add: false, 
            edit: false, 
            delete: false,
            reset_password: false,
            active: false,
            track_order: false,
            return_exchange: false,
            ndr: false,
            rto_return: false,
            order_again: false, // ✅ ADDED
            place_order: false
          });
        }
      } else {
        // Customer or other role - no permissions for admin/employee pages
        console.log('Customer or other role - no permissions');
        setPermissions({ 
          view: false, 
          add: false, 
          edit: false, 
          delete: false,
          reset_password: false,
          active: false,
          track_order: false,
          return_exchange: false,
          ndr: false,
          rto_return: false,
          order_again: false, // ✅ ADDED
          place_order: false
        });
      }
    } catch (error) {
      console.error('Error loading permissions:', error);
      setPermissions({ 
        view: false, 
        add: false, 
        edit: false, 
        delete: false,
        reset_password: false,
        active: false,
        track_order: false,
        return_exchange: false,
        ndr: false,
        rto_return: false,
        order_again: false, // ✅ ADDED
        place_order: false
      });
    } finally {
      setLoading(false);
    }
  };

  // ✅ UPDATED: hasPermission with all action types including order_again
  const hasPermission = (action: 'view' | 'add' | 'edit' | 'delete' | 'reset_password' | 'active' | 'track_order' | 'return_exchange' | 'ndr' | 'rto_return' | 'order_again' | 'place_order'): boolean => {
    return permissions[action];
  };

  // ✅ UPDATED: hasAnyPermission with all action types including order_again
  const hasAnyPermission = (actions: Array<'view' | 'add' | 'edit' | 'delete' | 'reset_password' | 'active' | 'track_order' | 'return_exchange' | 'ndr' | 'rto_return' | 'order_again' | 'place_order'>): boolean => {
    return actions.some(action => permissions[action]);
  };

  // ✅ UPDATED: hasAllPermissions with all action types including order_again
  const hasAllPermissions = (actions: Array<'view' | 'add' | 'edit' | 'delete' | 'reset_password' | 'active' | 'track_order' | 'return_exchange' | 'ndr' | 'rto_return' | 'order_again' | 'place_order'>): boolean => {
    return actions.every(action => permissions[action]);
  };

  return {
    permissions,
    loading,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions
  };
};

// Custom hook to check multiple modules at once
export const useMultiPermissions = (modules: string[]) => {
  const [allPermissions, setAllPermissions] = useState<Record<string, ModulePermissions>>({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadAllPermissions();
  }, [modules.join(',')]);

  const loadAllPermissions = async () => {
    try {
      setLoading(true);
      
      const userStr = localStorage.getItem('user');
      if (!userStr) {
        setLoading(false);
        return;
      }

      const user = JSON.parse(userStr);
      
      // Admin has all permissions (including new ones)
      if (user.role === 'admin') {
        const perms: Record<string, ModulePermissions> = {};
        modules.forEach(module => {
          perms[module] = { 
            view: true, 
            add: true, 
            edit: true, 
            delete: true,
            reset_password: true,
            active: true,
            track_order: true,
            return_exchange: true,
            ndr: true,
            rto_return: true,
            order_again: true, // ✅ ADDED
            place_order: true
          };
        });
        setAllPermissions(perms);
        setLoading(false);
        return;
      }

      // Employee - fetch from API
      if (user.role === 'employee') {
        const response = await apiService.getMyPermissions();
        
        if (response.success && response.data && response.data.permissions) {
          const perms: Record<string, ModulePermissions> = {};
          
          modules.forEach(module => {
            const modulePerm = response.data.permissions.find(
              (p: ModulePermission) => p.module === module
            );
            if (modulePerm) {
              // ✅ FIXED: Same mapping for multi-permissions
              perms[module] = {
                view: modulePerm.permissions.view || false,
                add: modulePerm.permissions.add || false,
                edit: modulePerm.permissions.edit || false,
                delete: modulePerm.permissions.delete || false,
                reset_password: (modulePerm.permissions as any).reset_password || (modulePerm.permissions as any).can_reset_password || false,
                active: (modulePerm.permissions as any).active || (modulePerm.permissions as any).can_toggle_active || false,
                track_order: (modulePerm.permissions as any).track_order || (modulePerm.permissions as any).can_track_order || false,
                return_exchange: (modulePerm.permissions as any).return_exchange || (modulePerm.permissions as any).can_return_exchange || false,
                ndr: (modulePerm.permissions as any).ndr || (modulePerm.permissions as any).can_ndr || false,
                rto_return: (modulePerm.permissions as any).rto_return || (modulePerm.permissions as any).can_rto || false,
                order_again: (modulePerm.permissions as any).order_again || (modulePerm.permissions as any).can_order_again || false, // ✅ ADDED
                place_order: (modulePerm.permissions as any).place_order || (modulePerm.permissions as any).can_place_order || false
              };
            } else {
              perms[module] = { 
                view: false, 
                add: false, 
                edit: false, 
                delete: false,
                reset_password: false,
                active: false,
                track_order: false,
                return_exchange: false,
                ndr: false,
                rto_return: false,
                order_again: false, // ✅ ADDED
                place_order: false
              };
            }
          });
          
          setAllPermissions(perms);
        }
      }
    } catch (error) {
      console.error('Error loading multiple permissions:', error);
    } finally {
      setLoading(false);
    }
  };

  // ✅ UPDATED: hasPermission with all action types including order_again
  const hasPermission = (module: string, action: 'view' | 'add' | 'edit' | 'delete' | 'reset_password' | 'active' | 'track_order' | 'return_exchange' | 'ndr' | 'rto_return' | 'order_again' | 'place_order'): boolean => {
    return allPermissions[module]?.[action] || false;
  };

  // ✅ UPDATED: hasAnyModulePermission with all action types including order_again
  const hasAnyModulePermission = (module: string, actions: Array<'view' | 'add' | 'edit' | 'delete' | 'reset_password' | 'active' | 'track_order' | 'return_exchange' | 'ndr' | 'rto_return' | 'order_again' | 'place_order'>): boolean => {
    return actions.some(action => allPermissions[module]?.[action] || false);
  };

  // ✅ UPDATED: hasAllModulePermissions with all action types including order_again
  const hasAllModulePermissions = (module: string, actions: Array<'view' | 'add' | 'edit' | 'delete' | 'reset_password' | 'active' | 'track_order' | 'return_exchange' | 'ndr' | 'rto_return' | 'order_again' | 'place_order'>): boolean => {
    return actions.every(action => allPermissions[module]?.[action] || false);
  };

  return {
    allPermissions,
    loading,
    hasPermission,
    hasAnyModulePermission,
    hasAllModulePermissions
  };
};

// Hook to check if user can access referral settings
export const useReferralPermissions = () => {
  const { permissions, loading, hasPermission } = usePermissions('referrals');
  
  return {
    loading,
    canView: hasPermission('view'),
    canEdit: hasPermission('edit'),
    canManage: hasPermission('edit'),
    hasAnyAccess: hasPermission('view') || hasPermission('edit')
  };
};

// ✅ UPDATED: Hook to check employee-specific permissions (reset_password, active)
export const useEmployeePermissions = (moduleName?: string) => {
  const { permissions, loading, hasPermission } = usePermissions(moduleName || 'employees');
  
  return {
    loading,
    canView: hasPermission('view'),
    canAdd: hasPermission('add'),
    canEdit: hasPermission('edit'),
    canDelete: hasPermission('delete'),
    canResetPassword: hasPermission('reset_password'),
    canToggleActive: hasPermission('active'),
    hasAnyAccess: hasPermission('view') || hasPermission('add') || hasPermission('edit') || hasPermission('delete') || hasPermission('reset_password') || hasPermission('active')
  };
};

// ✅ UPDATED: Hook to check order-specific permissions including order_again
export const useOrderPermissions = () => {
  const { permissions, loading: ordersLoading, hasPermission } = usePermissions('orders');
  // "Place Order" is a separate module ('place_order'); its access toggle is saved as 'view'
  const { permissions: placeOrderPerms, loading: placeOrderLoading } = usePermissions('place_order');
  const loading = ordersLoading || placeOrderLoading;
  const canPlaceOrder = !!placeOrderPerms.view || hasPermission('place_order');
  
  // ✅ DEBUG: Add debug logs to see what permissions are actually set
  console.log('🔍 Order Permissions State:', {
    loading,
    track_order: permissions.track_order,
    ndr: permissions.ndr,
    return_exchange: permissions.return_exchange,
    rto_return: permissions.rto_return,
    order_again: permissions.order_again, // ✅ ADDED: Debug log for order_again
    place_order: permissions.place_order,
    view: permissions.view
  });
  
  return {
    loading,
    canView: hasPermission('view'),
    canAdd: hasPermission('add'),
    canEdit: hasPermission('edit'),
    canDelete: hasPermission('delete'),
    canTrackOrder: hasPermission('track_order'),
    canProcessReturnExchange: hasPermission('return_exchange'),
    canManageNDR: hasPermission('ndr'),
    canManageRTO: hasPermission('rto_return'),
    canOrderAgain: hasPermission('order_again'), // ✅ ADDED: Order Again permission
    canPlaceOrder, // Place Order permission (module 'place_order' → view, or legacy orders.place_order)
    // Check if user has ANY order-related access
    canAccessOrders: hasPermission('view') || 
                     hasPermission('track_order') || 
                     hasPermission('ndr') || 
                     hasPermission('return_exchange') || 
                     hasPermission('rto_return') ||
                     hasPermission('order_again') ||
                     canPlaceOrder, // Include place_order
    // Check if user can perform any order action
    hasAnyOrderAction: hasPermission('track_order') || 
                       hasPermission('ndr') || 
                       hasPermission('return_exchange') || 
                       hasPermission('rto_return') ||
                       hasPermission('order_again') ||
                       canPlaceOrder // Include place_order
  };
};

export default usePermissions;